import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar/index';
import { EditOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import { Modal, Tooltip, Button, Divider, Select } from 'antd';
import qs from 'qs';
import { checkPermission } from '@/utils/permission';
import { cleanObject, renderBooleanIcon, renderServiceGroup, stripHtml } from '@/utils/helpers';
import HtmlParser from 'react-html-parser';
import TextTruncate from 'react-text-truncate';
import { fetchServiceList, fetchServiceDetail, deleteServices } from '@/redux/actions/serviceActions';
import NumberFormat from 'react-number-format';
import ServiceForm from './ServiceForm';
import CreatedAtCell from '@/components/CreatedAtCell';
import DynamicTable from '@/components/DynamicTable';
import ServiceGroupConfigurationModal from './ServiceGroupConfigurationModal';
import { getConfig } from '@/redux/actions/configActions';
import { getServiceGroups } from '@/utils/serviceGroups';

const title = 'Danh mục dịch vụ';
const path = 'services';
const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: `/${path}`, breadcrumbName: title },
];

export default function ServiceList() {
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();

    const items = useSelector(state => state.serviceReducer?.list.items);
    const config = useSelector(state => state.config || {});
    const serviceGroups = getServiceGroups(config);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isOpenForm, setIsOpenForm] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [isOpenConfiguration, setIsOpenConfiguration] = useState(false);

    const routeName = 'services';
    const allowCreate = checkPermission(routeName, 'create');
    const allowUpdate = checkPermission(routeName, 'update');
    const allowDelete = checkPermission(routeName, 'delete');

    const query = useMemo(() => {
        return qs.parse(location.search.slice(1));
    }, [location.search]);

    const loadData = useCallback((q) => {
        setIsLoading(true);
        dispatch(fetchServiceList(q)).finally(() => setIsLoading(false));
    }, [dispatch]);

    useEffect(() => {
        loadData(query);
    }, [query, loadData]);

    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false); // Trạng thái mở/đóng toàn bộ cây

    const collectExpandKeys = useCallback((nodes) => {
        let keys = [];
        for (let node of nodes || []) {
            if (node.children?.length) {
                keys.push(node.id);
                keys = keys.concat(collectExpandKeys(node.children));
            }
        }
        return keys;
    }, []);

    useEffect(() => {
        if (items?.length) {
            // const keys = collectExpandKeys(items); // mặc dịnh mở rộng toàn bộ cây
            const keys = collectExpandKeys([]);
            setExpandedRowKeys(keys);
            setIsExpanded(false);
        }
    }, [items, collectExpandKeys]);


    const toggleExpandAll = () => {
        if (isExpanded) {
            setExpandedRowKeys([]);
            setIsExpanded(false);
        } else {
            const keys = collectExpandKeys(items);
            setExpandedRowKeys(keys);
            setIsExpanded(true);
        }
    };


    const handleEdit = async (id) => {
        const res = await dispatch(fetchServiceDetail(id));
        setCurrentItem(res);
        setIsOpenForm(true);
    };

    const handleRemove = () => {
        dispatch(deleteServices(selectedRowKeys)).then(() => {
            setSelectedRowKeys([]);
            loadData(query);
            setTimeout(() => Modal.destroyAll(), 800);
        });
    };

    const handleFilterChange = (name, value) => {
        let q = { ...query, [name]: value };
        q = cleanObject(q);
        history.push({ pathname: location.pathname, search: qs.stringify(q) });
    };

    const handleCancel = () => {
        setCurrentItem(null);
        setIsOpenForm(false);
    };

    const handleCatalogChange = async () => {
        await dispatch(getConfig());
        loadData(query);
    };

    const columns = [
        { title: 'Tên', dataIndex: 'name', key: 'name' },
        { title: 'Mã', dataIndex: 'code', key: 'code', width: '150px' },
        {
            title: 'Giá',
            dataIndex: 'price',
            key: 'price',
            width: '120px',
            render: (text) => (
                <NumberFormat value={text} displayType={'text'} thousandSeparator={true} decimalScale={0} fixedDecimalScale={true} />
            )
        },
        {
            title: 'Ghi chú', dataIndex: 'note', key: 'note', width: '200px',
            render: (text) => (
                <div style={{ wordBreak: "break-all" }}>
                    <Tooltip title={HtmlParser(text)} placement="topLeft">
                        <TextTruncate line={1} truncateText="…" text={stripHtml(text)} />
                    </Tooltip>
                </div>
            )
        },
        {
            title: "Nhóm dịch vụ",
            dataIndex: "service_group_id",
            key: "service_group_id",
            width: '120px',
            render: (value, record) => renderServiceGroup(record?.service_group || value)
        },
        {
            title: 'Trạng thái', dataIndex: 'status', key: 'status', align: 'center', width: '100px',
            render: (text) => <>{renderBooleanIcon(text)}</>
        },
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: '50px'
        },
        {
            title: 'Ngày tạo',
            dataIndex: 'created_at',
            key: 'created_at',
            width: '220px',
            render: (text, record) => (
                <CreatedAtCell
                    createdAt={text}
                    user={record?.created_user}
                />
            )
        },
        {
            title: '#', align: 'center', width: '50px', key: 'action',
            render: (_, record) => (
                <Button size="small" type="dashed" className='btn-edit' icon={<EditOutlined />} onClick={() => handleEdit(record.id)} disabled={!allowUpdate}>Sửa</Button>
            )
        },
    ];

    return (
        <div>
            <PageTitle routes={routes} title={title} />
            <TableActionBar
                isShowAddButton={allowCreate}
                isShowDeleteButton={allowDelete}
                onAdd={() => setIsOpenForm(true)}
                disabled={!selectedRowKeys.length}
                onDelete={handleRemove}
                showSearch
                showFilter={false}
                activeFilter={query.status}
                onSearch={value => handleFilterChange('keyword', value)}
                defaultKeyword={query.keyword}
            >
                <Button icon={<ReloadOutlined />} loading={isLoading} onClick={() => loadData(query)}>Refresh</Button>
                <Divider type="vertical" />
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    placeholder="Lọc nhóm dịch vụ"
                    style={{ width: 220 }}
                    value={query.service_group_id ? Number(query.service_group_id) : undefined}
                    onChange={value => handleFilterChange('service_group_id', value)}
                >
                    {serviceGroups.map(item => (
                        <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>
                    ))}
                </Select>
                <Divider type="vertical" />
                <Button onClick={toggleExpandAll}>
                    {isExpanded ? 'Thu gọn tất cả' : 'Mở rộng tất cả'}
                </Button>
                <Divider type="vertical" />
                <Button icon={<SettingOutlined />} onClick={() => setIsOpenConfiguration(true)}>
                    Cấu hình
                </Button>
                {selectedRowKeys.length > 0 && <span><Divider type="vertical" />Đang chọn {selectedRowKeys.length} bản ghi </span>}
            </TableActionBar>

            <div className="has-custom-column-table-btn">
                <DynamicTable
                    rowKey="id"
                    dataSource={items || []}
                    columns={columns}
                    isLoading={isLoading}
                    pagination={false}
                    rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                    moduleKey="service_list"
                    scroll={{ x: 'max-content' }}
                    expandable={{
                        expandedRowKeys,
                        onExpandedRowsChange: (keys) => {
                            setExpandedRowKeys(keys);
                            setIsExpanded(keys.length > 0);
                        },
                        rowExpandable: record => !!record.children?.length,
                    }}
                />
            </div>
            <ServiceForm
                visible={isOpenForm}
                currentItem={currentItem}
                onCancel={() => handleCancel()}
                loadData={() => loadData(query)}
            />
            <ServiceGroupConfigurationModal
                visible={isOpenConfiguration}
                allowUpdate={allowUpdate}
                onCatalogChange={handleCatalogChange}
                onCancel={() => setIsOpenConfiguration(false)}
            />
        </div>
    );
}
